/**
 * JW Player Delivery API client
 * Handles config, playlists, media, and caching
 */

// ===== Types =====

export interface JWAppConfig {
  siteName: string;
  siteId: string;
  content: JWContentItem[];
  styling?: Record<string, unknown>;
}

export interface JWContentItem {
  contentId: string;
  type: string;
  featured?: boolean;
  title?: string;
}

export interface JWPlaylist {
  title: string;
  feedid: string;
  playlist: JWPlaylistItem[];
  kind?: string;
}

export interface JWPlaylistItem {
  mediaid: string;
  title: string;
  description: string;
  duration: number;
  image: string;
  images?: JWImage[];
  sources: JWSource[];
  tracks?: JWTrack[];
  tags?: string;
  feedid?: string;
  contentType?: string;
  content_source_type?: string;
  playlist?: string;
}

export interface JWImage {
  src: string;
  width: number;
  type: string;
}

export interface JWSource {
  file: string;
  type: string;
  label?: string;
  height?: number;
  width?: number;
}

export interface JWTrack {
  file: string;
  kind: string;
  label?: string;
}

// ===== Tile type compatible with existing components =====

export interface JWTile {
  src: string;
  tileSrc: string;
  backdrop: string;
  href: string;
  shortTitle: string;
  title: string;
  overview: string;
  item: JWPlaylistItem;
  entityInfo: { type: string; id: string };
  heroContent: { title: string; description: string };
}

export interface JWEpisode {
  episode_number: number;
  season_number: number;
  media_item: JWPlaylistItem | null;
}

export interface JWSeason {
  season_number: number;
  title: string;
  episodes: JWTile[];
}

// ===== Config =====

export const JW_APP_CONFIG_ID =
  (import.meta as any).env?.VITE_JW_APP_CONFIG_ID || "fv9lohqo";
export const JW_LIVE_PLAYLIST_ID =
  (import.meta as any).env?.VITE_JW_LIVE_PLAYLIST_ID || "ARzbAcEE";
export const JW_LIVE_MEDIA_ID =
  (import.meta as any).env?.VITE_JW_LIVE_MEDIA_ID || "8QM8TAII";

const CDN_BASE = "https://cdn.jwplayer.com";

// ===== Cache =====

const cache = new Map<string, { ts: number; data: unknown }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function getCached<T>(key: string): T | null {
  const entry = cache.get(key);
  if (entry && Date.now() - entry.ts < CACHE_TTL) {
    return entry.data as T;
  }
  return null;
}

function setCache(key: string, data: unknown) {
  cache.set(key, { ts: Date.now(), data });
}

// ===== API Functions =====

export async function fetchAppConfig(configId: string): Promise<JWAppConfig> {
  const cacheKey = `config:${configId}`;
  const cached = getCached<JWAppConfig>(cacheKey);
  if (cached) return cached;

  console.log(`[JW] Fetching app config: ${configId}`);
  const res = await fetch(`${CDN_BASE}/apps/configs/${configId}.json`);
  const data = await res.json();
  setCache(cacheKey, data);
  return data;
}

export async function fetchPlaylist(playlistId: string): Promise<JWPlaylist> {
  const cacheKey = `playlist:${playlistId}`;
  const cached = getCached<JWPlaylist>(cacheKey);
  if (cached) return cached;

  console.log(`[JW] Fetching playlist: ${playlistId}`);
  const res = await fetch(
    `${CDN_BASE}/v2/playlists/${playlistId}?format=json`
  );
  const data = await res.json();
  setCache(cacheKey, data);
  return data;
}

export async function fetchMedia(mediaId: string): Promise<JWPlaylist> {
  const cacheKey = `media:${mediaId}`;
  const cached = getCached<JWPlaylist>(cacheKey);
  if (cached) return cached;

  console.log(`[JW] Fetching media: ${mediaId}`);
  const res = await fetch(`${CDN_BASE}/v2/media/${mediaId}?format=json`);
  const data = await res.json();
  setCache(cacheKey, data);
  return data;
}

export async function fetchSeriesSeasons(item: JWPlaylistItem): Promise<JWSeason[]> {
  try {
    // 1. Try to fetch from the dedicated series endpoint
    const seriesId = item.mediaid;
    console.log("seriesId", seriesId);
    const res = await fetch(`${CDN_BASE}/apps/series/${seriesId}/episodes?format=json`);

    if (res.ok) {
      const data = await res.json();
      if (data && data.episodes && data.episodes.length > 0) {
        const seasonsMap = new Map<number, JWEpisode[]>();

        for (const ep of data.episodes) {
          if (ep.media_item) {
            const seasonNum = ep.season_number || 1;
            if (!seasonsMap.has(seasonNum)) {
              seasonsMap.set(seasonNum, []);
            }
            seasonsMap.get(seasonNum)!.push(ep);
          }
        }
        //console.log("seasonsMap", data.episodes);
        const seasons: JWSeason[] = [];
        for (const [seasonNum, eps] of seasonsMap.entries()) {
          // Sort episodes by episode_number ascending
          eps.sort((a, b) => a.episode_number - b.episode_number);

          const validItems = eps.map(e => e.media_item as JWPlaylistItem);

          seasons.push({
            season_number: seasonNum,
            title: `Season ${seasonNum}`,
            episodes: convertJWItemsToTiles(validItems)
          });
        }

        // Sort seasons by season_number ascending
        seasons.sort((a, b) => a.season_number - b.season_number);
        console.log("seasons", seasons);
        if (seasons.length > 0) return seasons;
      }
    }
  } catch (e) {
    console.warn(`[JW] Failed to fetch series episodes for ${item.mediaid}`, e);
  }

  // 2. Fallback to Hub logic (comma-separated playlist fields)
  if (item.playlist && typeof item.playlist === 'string') {
    const playlistIds = item.playlist.split(',');
    const seasons: JWSeason[] = [];

    for (let i = 0; i < playlistIds.length; i++) {
      try {
        const pl = await fetchPlaylist(playlistIds[i].trim());
        if (pl && pl.playlist && pl.playlist.length > 0) {
          seasons.push({
            season_number: i + 1,
            title: pl.title || `Season ${i + 1}`,
            episodes: convertJWItemsToTiles(pl.playlist)
          });
        }
      } catch (e) {
        console.warn(`[JW] Failed to fetch fallback playlist ${playlistIds[i]} for hub ${item.mediaid}`, e);
      }
    }
    return seasons;
  }

  // 3. Fallback to single item if neither works
  return [];
}

// ===== Helpers =====

export function posterUrl(mediaId: string, width: number = 320): string {
  return `${CDN_BASE}/v2/media/${mediaId}/poster.jpg?width=${width}`;
}

export function getPosterForWidth(
  item: JWPlaylistItem,
  width: number
): string {
  if (item.images?.length) {
    const imageOnly = item.images.filter((i) =>
      i.type?.startsWith("image/")
    );
    if (imageOnly.length) {
      const sorted = [...imageOnly].sort((a, b) => a.width - b.width);
      const match =
        sorted.find((i) => i.width >= width) || sorted[sorted.length - 1];
      if (match) return match.src.replace(/^http:\/\//i, 'https://');
    }
  }
  if (item.image) return item.image.replace(/^http:\/\//i, 'https://');
  return posterUrl(item.mediaid, width);
}

export function getManifestUrl(sources: JWSource[]): string | null {
  if (!sources) return null;
  // Priority: HLS > DASH > MP4
  const hls = sources.find(
    (s) => s.type === "application/vnd.apple.mpegurl" && s.file
  );
  if (hls) return hls.file;

  const dash = sources.find(
    (s) => s.type === "application/dash+xml" && s.file
  );
  if (dash) return dash.file;

  const mp4 = sources.find((s) => s.type === "video/mp4" && s.file);
  if (mp4) return mp4.file;

  return null;
}

function truncateString(str: string, maxLength: number): string {
  if (!str) return "";
  if (str.length > maxLength) {
    return str.substring(0, maxLength - 3) + "...";
  }
  return str;
}

export function convertJWItemsToTiles(items: JWPlaylistItem[]): JWTile[] {
  return items
    .filter((item) => item.mediaid)
    .map((item) => ({
      src: getPosterForWidth(item, 400),
      tileSrc: getPosterForWidth(item, 400),
      backdrop: getPosterForWidth(item, 720),
      href: "",
      shortTitle: truncateString(item.title, 30),
      title: item.title || "",
      overview: item.description || "",
      item,
      entityInfo: {
        type: "media",
        id: item.mediaid,
      },
      heroContent: {
        title: item.title || "",
        description: item.description || "",
      },
    }));
}
